import { useMemo, useState } from 'react';
import type { Character } from '@dcc/rules/domain/character.ts';
import type { EditDraft } from '@dcc/rules/app/characterEditService.ts';
import { addableSkills, draftFromCharacter } from '@dcc/rules/app/characterEditService.ts';
import type { CustomSkillDraft } from '@dcc/rules/rules/customSkills.ts';
import { contentRegistry, contentVersion } from '@dcc/rules/content/dccCoreContent.ts';
import { STAT_IDS, STAT_LABELS, type StatId } from '@dcc/rules/domain/stats.ts';
import { CustomSpellForm } from './CustomSpellForm.tsx';

export interface CreateCustomSkillResult {
  ok: boolean;
  draft?: EditDraft;
  message: string;
}

export function EditSheetPanel(props: {
  character: Character;
  busy: boolean;
  onSave: (draft: EditDraft) => Promise<boolean>;
  onEditingChange?: (editing: boolean) => void;
  onCreateCustom: (draft: EditDraft, custom: CustomSkillDraft, startingRank: string) => Promise<CreateCustomSkillResult>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EditDraft>(() => draftFromCharacter(props.character));
  const [message, setMessage] = useState('');
  const fingerprint = (character: Character) => JSON.stringify({ ...draftFromCharacter(character), floor: 0 });
  const [baseFingerprint, setBaseFingerprint] = useState(() => fingerprint(props.character));
  const changedElsewhere = editing && fingerprint(props.character) !== baseFingerprint;
  const originalTotal = STAT_IDS.reduce((sum, id) => sum + props.character.unenhancedStats[id], 0);
  const draftTotal = STAT_IDS.reduce((sum, id) => sum + draft.stats[id], 0);
  const statsChanged = STAT_IDS.some(id => draft.stats[id] !== props.character.unenhancedStats[id]);
  const invalidRedistribution = draftTotal !== originalTotal || (statsChanged && draft.level !== props.character.level);
  const [customOpen, setCustomOpen] = useState(false);
  const [spellOpen, setSpellOpen] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customKind, setCustomKind] = useState<'utility' | 'passive'>('utility');
  const [customStat, setCustomStat] = useState<StatId>('dex');
  const [customDescription, setCustomDescription] = useState('');
  const [customRank, setCustomRank] = useState('1');
  const [existingBookId, setExistingBookId] = useState('');
  const [existingBookRank, setExistingBookRank] = useState('1');
  const [existingCustomId, setExistingCustomId] = useState('');
  const [existingCustomRank, setExistingCustomRank] = useState('1');
  const [pendingDeleteSkillId, setPendingDeleteSkillId] = useState<string | null>(null);

  const availableSkills = useMemo(
    () => addableSkills(draft, contentRegistry),
    [draft, customOpen, message, contentVersion],
  );
  const availableBookSkills = availableSkills.filter((skill) => skill.source.book !== 'custom');
  const availableCustomSkills = availableSkills.filter((skill) => skill.source.book === 'custom');

  function resetFromSaved() {
    setDraft(draftFromCharacter(props.character));
    setBaseFingerprint(fingerprint(props.character));
    setMessage('');
    setCustomOpen(false);
    setSpellOpen(false);
    setCustomName('');
    setCustomKind('utility');
    setCustomStat('dex');
    setCustomDescription('');
    setCustomRank('1');
    setExistingBookId('');
    setExistingBookRank('1');
    setExistingCustomId('');
    setExistingCustomRank('1');
    setPendingDeleteSkillId(null);
  }

  function openEditor() {
    resetFromSaved();
    setEditing(true);
    props.onEditingChange?.(true);
  }

  function patchStat(stat: StatId, raw: string) {
    const value = Number(raw);
    setDraft((current) => ({
      ...current,
      stats: { ...current.stats, [stat]: Number.isFinite(value) ? Math.trunc(value) : current.stats[stat] },
    }));
  }

  function patchSkillRank(skillId: string, raw: string) {
    const value = Number(raw);
    if (!Number.isFinite(value)) return;
    setDraft((current) => ({
      ...current,
      skills: current.skills.map((entry) => entry.skillId === skillId ? { ...entry, rank: Math.trunc(value) } : entry),
    }));
  }

  async function createCustomSkill() {
    setMessage('');
    const custom: CustomSkillDraft = {
      name: customName,
      kind: customKind,
      commonStat: customKind === 'passive' ? null : customStat,
      description: customDescription,
      attack: null,
    };
    const result = await props.onCreateCustom(draft, custom, customRank);
    setMessage(result.message);
    if (!result.ok || !result.draft) return;
    setDraft(result.draft);
    setCustomName('');
    setCustomKind('utility');
    setCustomStat('dex');
    setCustomDescription('');
    setCustomRank('1');
    setCustomOpen(false);
  }


  function requestDeleteSkill(skillId: string) {
    setPendingDeleteSkillId(skillId);
    setMessage('');
  }

  function cancelDeleteSkill() {
    setPendingDeleteSkillId(null);
  }

  function confirmDeleteSkill() {
    if (!pendingDeleteSkillId) return;
    const skill = contentRegistry.getSkill(pendingDeleteSkillId);
    setDraft((current) => ({
      ...current,
      skills: current.skills.filter((entry) => entry.skillId !== pendingDeleteSkillId),
    }));
    setMessage(`${skill?.name ?? pendingDeleteSkillId} is removed from this edit. Save Sheet Changes to make it permanent.`);
    setPendingDeleteSkillId(null);
  }

  function addExistingBookSkill() {
    const rank = Number(existingBookRank);
    if (!existingBookId || !Number.isInteger(rank) || rank < 1) {
      setMessage('Choose a book Skill and enter a whole-number starting Rank of at least 1.');
      return;
    }
    setDraft((current) => ({
      ...current,
      skills: [...current.skills, { skillId: existingBookId, rank }],
    }));
    const name = contentRegistry.getSkill(existingBookId)?.name ?? existingBookId;
    setMessage(`${name} is ready to add. Save Sheet Changes to make it permanent.`);
    setExistingBookId('');
    setExistingBookRank('1');
  }

  function addExistingCustomSkill() {
    const rank = Number(existingCustomRank);
    if (!existingCustomId || !Number.isInteger(rank) || rank < 1) {
      setMessage('Choose a custom Skill and enter a whole-number starting Rank of at least 1.');
      return;
    }
    setDraft((current) => ({
      ...current,
      skills: [...current.skills, { skillId: existingCustomId, rank }],
    }));
    const name = contentRegistry.getSkill(existingCustomId)?.name ?? existingCustomId;
    setMessage(`${name} is ready to add. Save Sheet Changes to make it permanent.`);
    setExistingCustomId('');
    setExistingCustomRank('1');
  }

  if (!editing) {
    return (
      <section className="dcc-edit-sheet-launch">
        <button className="dcc-edit-sheet-button" disabled={props.busy} onClick={openEditor}>Edit Sheet</button>
      </section>
    );
  }

  return (
    <section className="dcc-edit-sheet" aria-label="Edit Sheet">
      {changedElsewhere && <div className="dcc-creator-warn" role="alert">The saved Sheet changed while you were editing. Your draft is still here. Copy any changes you want to keep, then load the current Sheet before saving.<button type="button" disabled={props.busy} onClick={() => { if (window.confirm('Replace this unsaved edit draft with the current saved Sheet?')) resetFromSaved(); }}>Load current Sheet</button></div>}
      <div className="dcc-edit-sheet-heading">
        <h2>Edit Sheet</h2>
        <span>Floor {props.character.floor} · GM controlled</span>
      </div>
      <p className="dcc-creator-help">
        These are permanent base values. Temporary bonuses from gear, Buffs, Debuffs, and other effects are not included here.
      </p>

      <p className="dcc-creator-help">Redistribute attributes only outside combat. Total: {draftTotal} / {originalTotal}. Keep the total unchanged; spend earned points through Advancement.</p>
      {invalidRedistribution && <p role="alert" className="dcc-creator-warn">Keep the original total attribute points and save level changes separately before saving this rotation.</p>}
      <div className="dcc-edit-sheet-grid">
        <label>Level
          <input
            type="number"
            min={1}
            step={1}
            value={draft.level}
            disabled={props.busy}
            onChange={(event) => setDraft((current) => ({ ...current, level: Math.trunc(Number(event.target.value)) }))}
          />
        </label>
        {STAT_IDS.map((stat) => (
          <label key={stat}>{STAT_LABELS[stat].abbr}
            <input
              type="number"
              min={1}
              step={1}
              value={draft.stats[stat]}
              disabled={props.busy}
              onChange={(event) => patchStat(stat, event.target.value)}
            />
          </label>
        ))}
      </div>

      <div className="dcc-edit-sheet-section">
        <h3>Skill Ranks</h3>
        <p className="dcc-creator-help">Edit the permanent Rank only. Temporary Rank bonuses are intentionally excluded.</p>
        {draft.skills.length === 0 ? <p className="dcc-empty">No Skills on this crawler.</p> : (
          <div className="dcc-edit-skill-list">
            {draft.skills.map((entry) => {
              const skill = contentRegistry.getSkill(entry.skillId);
              return (
                <div key={entry.skillId} className="dcc-edit-skill-row">
                  <span>{skill?.name ?? entry.skillId}{skill?.source.book === 'custom' ? ' · Custom' : ''}</span>
                  <input
                    aria-label={`${skill?.name ?? entry.skillId} permanent rank`}
                    type="number"
                    min={1}
                    step={1}
                    value={entry.rank}
                    disabled={props.busy}
                    onChange={(event) => patchSkillRank(entry.skillId, event.target.value)}
                  />
                  <button
                    type="button"
                    className="dcc-edit-skill-delete"
                    disabled={props.busy}
                    onClick={() => requestDeleteSkill(entry.skillId)}
                  >Delete</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {pendingDeleteSkillId && (
        <div className="dcc-edit-skill-confirm-backdrop" onClick={cancelDeleteSkill}>
          <div
            className="dcc-edit-skill-confirm"
            role="dialog"
            aria-modal="true"
            aria-label="Confirm Skill deletion"
            onClick={(event) => event.stopPropagation()}
          >
            <button type="button" className="dcc-edit-skill-confirm-close" aria-label="Close" onClick={cancelDeleteSkill}>×</button>
            <strong>Delete {contentRegistry.getSkill(pendingDeleteSkillId)?.name ?? pendingDeleteSkillId}?</strong>
            <p>Choose No or close this box to keep it. Even after Yes, the deletion is not permanent until you press Save Sheet Changes.</p>
            <div className="dcc-action-buttons">
              <button type="button" disabled={props.busy} onClick={confirmDeleteSkill}>Yes, delete Skill</button>
              <button type="button" className="dcc-creator-secondary" disabled={props.busy} onClick={cancelDeleteSkill}>No, keep it</button>
            </div>
          </div>
        </div>
      )}

      <div className="dcc-edit-sheet-section">
        <h3>Add Book Skill</h3>
        <p className="dcc-creator-help">Award a Skill that already exists in the loaded DCC rulebooks. Skills this crawler already has are hidden.</p>
        {availableBookSkills.length > 0 ? (
          <div className="dcc-custom-skill-existing">
            <label>Book Skill
              <select value={existingBookId} disabled={props.busy} onChange={(event) => setExistingBookId(event.target.value)}>
                <option value="">Choose a Skill…</option>
                {availableBookSkills.map((skill) => <option key={skill.id} value={skill.id}>{skill.name}</option>)}
              </select>
            </label>
            <label>Starting Rank
              <input type="number" min={1} step={1} value={existingBookRank} disabled={props.busy}
                onChange={(event) => setExistingBookRank(event.target.value)} />
            </label>
            <button className="dcc-creator-secondary" disabled={props.busy || !existingBookId} onClick={addExistingBookSkill}>Add to this edit</button>
          </div>
        ) : (
          <p className="dcc-empty">This crawler already has every legal book Skill.</p>
        )}
      </div>

      <div className="dcc-edit-sheet-section">
        <h3>Custom Skills</h3>
        {!spellOpen ? <button className="dcc-creator-secondary" disabled={props.busy} onClick={() => { setSpellOpen(true); setMessage(''); }}>Add New Custom Spell</button> :
          <CustomSpellForm busy={props.busy} onCancel={() => setSpellOpen(false)} onCreate={async (custom, rank) => {
            const result = await props.onCreateCustom(draft, custom, rank); setMessage(result.message);
            if (!result.ok || !result.draft) return false; setDraft(result.draft); return true;
          }} />}
        {availableCustomSkills.length > 0 && (
          <div className="dcc-custom-skill-existing">
            <label>Existing custom Skill
              <select value={existingCustomId} disabled={props.busy} onChange={(event) => setExistingCustomId(event.target.value)}>
                <option value="">Choose…</option>
                {availableCustomSkills.map((skill) => <option key={skill.id} value={skill.id}>{skill.name}</option>)}
              </select>
            </label>
            <label>Starting Rank
              <input type="number" min={1} step={1} value={existingCustomRank} disabled={props.busy}
                onChange={(event) => setExistingCustomRank(event.target.value)} />
            </label>
            <button className="dcc-creator-secondary" disabled={props.busy || !existingCustomId} onClick={addExistingCustomSkill}>Add to this edit</button>
          </div>
        )}

        {!customOpen ? (
          <button className="dcc-creator-secondary" disabled={props.busy} onClick={() => { setCustomOpen(true); setMessage(''); }}>
            Add New Custom Skill
          </button>
        ) : (
          <div className="dcc-custom-skill-form">
            <label>Skill name
              <input maxLength={60} value={customName} disabled={props.busy} onChange={(event) => setCustomName(event.target.value)} />
            </label>
            <label>Type
              <select value={customKind} disabled={props.busy} onChange={(event) => setCustomKind(event.target.value as 'utility' | 'passive')}>
                <option value="utility">Utility</option>
                <option value="passive">Passive</option>
              </select>
            </label>
            {customKind === 'utility' && (
              <label>Common Stat
                <select value={customStat} disabled={props.busy} onChange={(event) => setCustomStat(event.target.value as StatId)}>
                  {STAT_IDS.map((stat) => <option key={stat} value={stat}>{STAT_LABELS[stat].full}</option>)}
                </select>
              </label>
            )}
            <label>Starting Rank
              <input type="number" min={1} step={1} value={customRank} disabled={props.busy} onChange={(event) => setCustomRank(event.target.value)} />
            </label>
            <label>Description
              <textarea rows={3} maxLength={2000} value={customDescription} disabled={props.busy} onChange={(event) => setCustomDescription(event.target.value)} />
            </label>
            <div className="dcc-action-buttons">
              <button disabled={props.busy || customName.trim() === ''} onClick={() => void createCustomSkill()}>Create Skill</button>
              <button className="dcc-creator-secondary" disabled={props.busy} onClick={() => { setCustomOpen(false); setMessage(''); }}>Cancel New Skill</button>
            </div>
          </div>
        )}
      </div>

      <label className="dcc-edit-sheet-notes">Notes
        <textarea rows={3} value={draft.notes} disabled={props.busy} onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))} />
      </label>

      {message ? <p className="dcc-action-note" role="status">{message}</p> : null}

      <div className="dcc-action-buttons dcc-edit-sheet-save">
        <button disabled={props.busy || changedElsewhere || invalidRedistribution} onClick={async () => {
          if (changedElsewhere || invalidRedistribution) return;
          setMessage('');
          const saved = await props.onSave(draft);
          if (saved) {
            setEditing(false);
            props.onEditingChange?.(false);
            setMessage('');
          }
        }}>Save Sheet Changes</button>
        <button className="dcc-creator-secondary" disabled={props.busy} onClick={() => { resetFromSaved(); setEditing(false); props.onEditingChange?.(false); }}>Cancel</button>
      </div>
    </section>
  );
}
